import { type Ref, ref, computed } from "vue";
import apiSkobo from "@/helpers/api/apiSkobo";
import { useSectionsStore } from "@/stores/sections";
import { useNavigationStore } from "@/stores/navigation";

import Tile from "@/models/Tiles/Tile";

interface CatalogItem {
  groupName?: string;
  [key: string]: unknown;
}

interface CatalogGroup {
  title: string;
  items: CatalogItem[];
}

interface BreadcrumbEntry {
  name: string;
  routeName: string;
  routeParams: { groupId: string };
}

interface NavigationLikeItem {
  uri?: { action?: string; uri?: string };
  description?: string;
}

/**
 * Parse menuid - if set return it - if not return "root"
 */
const parseMenuId = (menuId: string | undefined): string => {
  return !menuId ? "root" : menuId;
};

/**
 * Search for tile in state tiles
 */
const searchForTileInStateTiles = (map: Record<string, Tile[]>, uri: string): BreadcrumbEntry[] => {
  const mapKeys = Object.keys(map);

  for (let i = 0; i < mapKeys.length; i++) {
    const tiles = map[mapKeys[i]];
    const tile = tiles.find((t: Tile) => uri.startsWith(t.uri.uri));

    if (tile) {
      if (mapKeys[i] === "UserMainTiles" || mapKeys[i] === "UserSectionTiles") {
        return [
          {
            name: tile.title,
            routeName: "tiles",
            routeParams: { groupId: tile.uri.uri.replace("/tiles/", "") }
          }
        ];
      }
      const parents = searchForTileInStateTiles(map, `/tiles/${mapKeys[i]}`);
      parents.push({
        name: tile.title,
        routeName: "tiles",
        routeParams: { groupId: mapKeys[i] }
      });
      return parents;
    }
  }
  return [];
};

export const useTilesComposable = () => {
  // State refs
  const tilesMap: Ref<Record<string, Tile[]>> = ref({});
  const sectionTilesMap: Ref<Record<string, Record<string, Tile[]>>> = ref({});
  const tilesCatalog: Ref<CatalogGroup[]> = ref([]);

  // Actions
  /**
   * Force load tiles for dashboard
   */
  const reloadTiles = async (collectionId: string) => {
    const response = await apiSkobo.get(`/Tile/${collectionId}`);
    const tilesList = response.data.values.map((t: unknown) => Tile.parseFromApi(t));
    tilesMap.value[collectionId] = tilesList;
  };

  /**
   * Load tiles for dashboard if not present
   */
  const loadTiles = async (collectionId: string = "UserMainTiles") => {
    if (tilesMap.value[collectionId]) {
      return;
    }

    await reloadTiles(collectionId);

    // Check if UserMainTiles is available - else load it to get base
    if (!tilesMap.value["UserMainTiles"]) {
      await reloadTiles("UserMainTiles");
    }
  };

  const reloadTilesForSection = async (sectionId: string, collectionId?: string) => {
    const tilesForSectionResponse = await apiSkobo.get(
      `/Tile/Section/${
        (sectionId !== undefined ? sectionId : "") + (collectionId !== undefined ? "/" + collectionId : "")
      }`
    );

    const tilesList = tilesForSectionResponse?.data?.values?.map((t: unknown) => Tile.parseFromApi(t));

    if (!sectionTilesMap.value[sectionId]) {
      sectionTilesMap.value[sectionId] = {};
    }

    sectionTilesMap.value[sectionId][parseMenuId(collectionId)] = tilesList;
  };

  /**
   * Load tiles for section
   */
  const loadTilesForSection = async (sectionId: string, collectionId?: string) => {
    if (sectionTilesMap.value[sectionId + collectionId]) {
      return;
    }

    return await reloadTilesForSection(sectionId, collectionId);
  };

  /**
   * Load tiles for all my sections
   */
  const loadTilesForMySections = async (collectionId?: string) => {
    const sectionsStore = useSectionsStore();
    const mySectionsIds = Object.keys(sectionsStore.mySections);

    mySectionsIds.forEach(sectionId => {
      loadTilesForSection(sectionId, collectionId);
    });
  };

  /**
   * Load tiles for all archived my sections
   */
  const loadTilesForMyArchivedSections = async (collectionId?: string) => {
    const sectionsStore = useSectionsStore();
    const mySectionsIds = Object.keys(sectionsStore.myArchivedSections);

    mySectionsIds.forEach(sectionId => {
      loadTilesForSection(sectionId, collectionId);
    });
  };

  /**
   * Load entire tiles catalog
   */
  const loadCatalog = async () => {
    try {
      if (tilesCatalog.value.length === 0) {
        const response = await apiSkobo.get("/Tile/Catalog/All");

        const catalogList: CatalogGroup[] = response.data.groups.map((g: string) => ({
          title: g,
          items: response.data.values.filter((v: CatalogItem) => v.groupName === g)
        }));

        tilesCatalog.value = catalogList;
      }
      // eslint-disable-next-line no-empty
    } catch {}
  };

  /**
   * Load entire tiles hierarchy (UserMainTiles and all tiles that are linked to it)
   */
  const loadEntireTilesHierarchy = async () => {
    // Load UserMainTiles
    await loadTiles();
    tilesMap.value["UserMainTiles"].forEach(async (tile: Tile) => {
      if (tile.uri.uri.startsWith("/tiles/")) {
        await loadTiles(tile.uri.uri.replace("/tiles/", ""));
      }
    });
  };

  // Computed/function getters
  const sectionTiles = computed(
    () => (sectionId: string, collectionId?: string) =>
      sectionTilesMap.value[sectionId] ? sectionTilesMap.value[sectionId][parseMenuId(collectionId)] || [] : []
  );

  const tiles = computed(() => (collectionId?: string) => {
    const id = collectionId ? collectionId : "UserMainTiles";
    return tilesMap.value[id] ? tilesMap.value[id] : [];
  });

  const groupTitle = computed(() => (collectionId: string) => {
    const linksToGroup: NavigationLikeItem[] = [];

    // Look in tiles
    for (const key in tilesMap.value) {
      linksToGroup.push(
        ...tilesMap.value[key].filter(
          (v: Tile) =>
            v.uri &&
            v.uri.action === "app" &&
            (v.uri.uri === "/tiles/" + collectionId || v.uri.uri === "tiles/" + collectionId)
        )
      );
    }

    if (linksToGroup.length > 0) {
      return linksToGroup[0].description;
    }

    // Look in navigation menu (should probably be merged with navigation and tiles)
    const navigationStore = useNavigationStore();
    linksToGroup.push(
      ...navigationStore.leftMenu.filter(
        (v: NavigationLikeItem) =>
          v.uri &&
          v.uri.action === "app" &&
          (v.uri.uri === "/tiles/" + collectionId || v.uri.uri === "tiles/" + collectionId)
      )
    );

    if (linksToGroup.length > 0) {
      return linksToGroup[0].description;
    }

    return "{Menu.Home}";
  });

  const catalog = computed(() => tilesCatalog.value);

  const tilesBreadCrumbFromUri = computed(() => (uri: string) => {
    return searchForTileInStateTiles(tilesMap.value, uri) || [];
  });

  return {
    tilesMap,
    sectionTilesMap,
    tilesCatalog,
    loadTiles,
    reloadTiles,
    loadTilesForSection,
    reloadTilesForSection,
    loadTilesForMySections,
    loadTilesForMyArchivedSections,
    loadCatalog,
    loadEntireTilesHierarchy,
    sectionTiles,
    tiles,
    groupTitle,
    catalog,
    tilesBreadCrumbFromUri
  };
};
